import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import {
  User,
  MessageCircle,
  Scale,
  ExternalLink,
  LogOut
} from "lucide-react";

interface OverflowMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItemProps {
  to?: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  isExternal?: boolean;
}

const MenuItem = ({ to, icon, label, onClick, isExternal = false }: MenuItemProps) => {
  const baseClasses = `
    flex items-center px-4 py-3 space-x-3 
    hover:bg-muted transition-colors duration-150
    focus:outline-none focus:bg-muted
    border-b border-gray-100 last:border-b-0
  `;

  const content = (
    <>
      <div className="w-5 h-5 text-muted-foreground">
        {icon}
      </div>
      <span className="text-base font-medium text-foreground">{label}</span>
      {isExternal && (
        <ExternalLink className="w-4 h-4 text-muted-foreground ml-auto" />
      )}
    </>
  );

  if (to && !isExternal) {
    return (
      <Link to={to} className={baseClasses} onClick={onClick}>
        {content}
      </Link>
    );
  }

  if (to && isExternal) {
    return (
      <a 
        href={to} 
        target="_blank" 
        rel="noopener noreferrer" 
        className={baseClasses}
        onClick={onClick}
      >
        {content}
      </a>
    );
  }

  return (
    <button className={`${baseClasses} w-full text-left`} onClick={onClick}>
      {content}
    </button>
  );
};

const OverflowMenu = ({ isOpen, onClose }: OverflowMenuProps) => {
  const { logout } = useAuth();
  const [isAnimating, setIsAnimating] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Блокируем скролл body при открытии меню
      document.body.style.overflow = 'hidden';
    } else if (isAnimating) {
      // Восстанавливаем скролл при закрытии с небольшой задержкой для анимации
      setTimeout(() => {
        document.body.style.overflow = '';
        setIsAnimating(false);
      }, 300);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isAnimating]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Закрываем при клике на backdrop или основной контейнер
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    // Дополнительный обработчик для backdrop
    e.stopPropagation();
    onClose();
  };

  // Обработчики для свайпа вниз
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isDownSwipe = distance < -50; // Свайп вниз на 50px+
    
    if (isDownSwipe) {
      onClose();
    }
  };

  const handleMenuClick = () => {
    onClose();
  };

  const handleOpenInBrowser = () => {
    // Для Telegram WebApp - открываем текущий URL в браузере
    const currentUrl = window.location.href;
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.openLink(currentUrl);
    } else {
      // Fallback для обычных браузеров
      window.open(currentUrl, '_blank');
    }
    onClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      onClose();

      // Закрываем Telegram mini app если открыто в контексте Telegram
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.close();
      }
    } catch (error) {
      console.error('Ошибка при выходе:', error);
      onClose();
    }
  };

  // Полностью удаляем компонент из DOM когда он не нужен
  if (!isOpen && !isAnimating) return null;

  return (
    <div
      className={`
        fixed inset-0 z-[60] flex items-end justify-center
        transition-all duration-300 ease-smooth
        ${isOpen ? 'opacity-100' : 'opacity-0'}
      `}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Дополнительное меню"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm cursor-pointer"
        aria-hidden="true"
        onClick={handleOverlayClick}
      />
      
      {/* Menu Container */}
      <div
        className={`
          relative w-full max-w-md mx-4 mb-4 bg-card rounded-2xl shadow-2xl
          transform transition-transform duration-300 ease-smooth
          ${isOpen ? 'translate-y-0' : 'translate-y-full'}
          max-h-[70vh] overflow-hidden
        `}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Handle для свайпа */}
        <div className="flex justify-center py-3">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>
        
        {/* Header */}
        <div className="px-4 pb-2">
          <h2 className="text-lg font-semibold text-foreground">Дополнительно</h2>
        </div>
        
        {/* Menu Items */}
        <div className="pb-4">
          <MenuItem
            to="/profile"
            icon={<User />}
            label="Профиль"
            onClick={handleMenuClick}
          />
          <MenuItem
            to="/support"
            icon={<MessageCircle />}
            label="Поддержка"
            onClick={handleMenuClick}
          />
          <MenuItem
            to="/legal"
            icon={<Scale />}
            label="Правовая информация"
            onClick={handleMenuClick}
          />
          <MenuItem
            icon={<ExternalLink />}
            label="Открыть в браузере"
            onClick={handleOpenInBrowser}
            isExternal={true}
          />
          <MenuItem
            icon={<LogOut />}
            label="Выход"
            onClick={handleLogout}
          />
        </div>
        
        {/* Safe area для iPhone */}
        <div className="h-[env(safe-area-inset-bottom)]" />
      </div>
    </div>
  );
};

export default OverflowMenu;